import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { PublicMenuItem } from '@/features/themes/lib/useSiteMenu';
import { buildMenuHref, isInternalItem } from '../lib/menuHref';

interface PublicMenuNavProps {
  menu: PublicMenuItem[];
  siteSlug: string;
  style?: 'horizontal' | 'vertical';
  align?: 'left' | 'center' | 'right';
  dropdownRadius?: 'none' | 'sm' | 'md' | 'lg';
  className?: string;
  onNavigate?: () => void;
  /** Sobrescreve cor/tamanho/peso dos itens — vem do bloco Menu, ausente no header padrão do site. */
  textStyle?: CSSProperties;
  backgroundColor?: string;
  backgroundOpacity?: number;
  dropdownBackgroundColor?: string;
  dropdownHoverBackgroundColor?: string;
  dropdownTextColor?: string;
}

const DROPDOWN_RADIUS_CLASS: Record<NonNullable<PublicMenuNavProps['dropdownRadius']>, string> = {
  none: 'rounded-none',
  sm: 'rounded-lg',
  md: 'rounded-xl',
  lg: 'rounded-2xl',
};

/**
 * Navegação do menu do site — reusada pelo header padrão embutido e pelo
 * bloco "Menu" (para headers/footers/páginas customizados via editor).
 */
export function PublicMenuNav({
  menu,
  siteSlug,
  style = 'horizontal',
  align = 'center',
  dropdownRadius = 'none',
  className,
  onNavigate,
  textStyle,
  backgroundColor,
  backgroundOpacity = 100,
  dropdownBackgroundColor,
  dropdownHoverBackgroundColor,
  dropdownTextColor,
}: PublicMenuNavProps) {
  if (menu.length === 0) return null;

  const backgroundStyle = getMenuBackgroundStyle(backgroundColor, backgroundOpacity);

  if (style === 'vertical') {
    return (
      <nav className={`flex flex-col gap-1 ${className ?? ''}`} style={backgroundStyle}>
        {menu.map((item) => (
          <div key={item.id}>
            <MenuLink item={item} siteSlug={siteSlug} onNavigate={onNavigate} style={textStyle} className="block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" />
            {item.children.length > 0 && (
              <div className="ml-3 border-l border-slate-100 pl-3">
                {item.children.map((child) => (
                  <MenuLink key={child.id} item={child} siteSlug={siteSlug} onNavigate={onNavigate} style={textStyle} className="block rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-50" />
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    );
  }

  const alignClass = align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';
  return (
    <nav className={`flex flex-wrap items-center gap-1 ${alignClass} ${className ?? ''}`} style={backgroundStyle}>
      {menu.map((item) => (
        <MenuNode
          key={item.id}
          item={item}
          siteSlug={siteSlug}
          dropdownRadius={dropdownRadius}
          dropdownBackgroundColor={dropdownBackgroundColor}
          dropdownHoverBackgroundColor={dropdownHoverBackgroundColor}
          dropdownTextColor={dropdownTextColor}
          textStyle={textStyle}
        />
      ))}
    </nav>
  );
}

export function getMenuBackgroundStyle(color?: string, opacity = 100): CSSProperties | undefined {
  if (!color) return undefined;
  const value = Math.max(0, Math.min(100, opacity));
  return { backgroundColor: value === 100 ? color : `color-mix(in srgb, ${color} ${value}%, transparent)` };
}

export function getDropdownHoverStyle(hoverColor?: string, dropdownColor?: string): CSSProperties {
  return {
    '--menu-submenu-hover-background': hoverColor || dropdownColor || '#f8fafc',
  } as CSSProperties;
}

/** Item de topo: link simples, ou botão com dropdown quando tem filhos. */
export function MenuNode({
  item,
  siteSlug,
  dropdownRadius = 'none',
  dropdownBackgroundColor,
  dropdownHoverBackgroundColor,
  dropdownTextColor,
  textStyle,
}: {
  item: PublicMenuItem;
  siteSlug: string;
  dropdownRadius?: PublicMenuNavProps['dropdownRadius'];
  dropdownBackgroundColor?: string;
  dropdownHoverBackgroundColor?: string;
  dropdownTextColor?: string;
  textStyle?: CSSProperties;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const menuItemRef = useRef<HTMLDivElement>(null);
  const dropdownTextStyle = { ...textStyle, color: dropdownTextColor || textStyle?.color };
  const dropdownHoverStyle = getDropdownHoverStyle(dropdownHoverBackgroundColor, dropdownBackgroundColor);

  useEffect(() => {
    if (!isOpen) return;

    const closeWhenClickingOutside = (event: PointerEvent) => {
      if (!menuItemRef.current?.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener('pointerdown', closeWhenClickingOutside);
    return () => document.removeEventListener('pointerdown', closeWhenClickingOutside);
  }, [isOpen]);

  if (item.children.length === 0) {
    return <MenuLink item={item} siteSlug={siteSlug} style={dropdownTextStyle} className="rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:text-slate-950" />;
  }
  return (
    <div ref={menuItemRef} className="group relative">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={() => setIsOpen((open) => !open)}
        className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:text-slate-950"
        style={dropdownTextStyle}
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : 'group-hover:rotate-180'}`} />
      </button>
      <div
        style={dropdownBackgroundColor ? { backgroundColor: dropdownBackgroundColor } : undefined}
        className={`absolute left-0 top-full min-w-48 border border-slate-200/80 bg-white p-1.5 shadow-xl transition-all ${
          isOpen ? 'visible opacity-100' : 'invisible opacity-0 group-hover:visible group-hover:opacity-100'
        } ${DROPDOWN_RADIUS_CLASS[dropdownRadius ?? 'none']}`}
      >
        {/* item pai também navegável, se tiver destino próprio */}
        <MenuLink item={item} siteSlug={siteSlug} onNavigate={() => setIsOpen(false)} style={{ ...dropdownTextStyle, ...dropdownHoverStyle }} className="block rounded-lg px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-[var(--menu-submenu-hover-background)]" />
        {item.children.map((child) => (
          <MenuLink key={child.id} item={child} siteSlug={siteSlug} onNavigate={() => setIsOpen(false)} style={{ ...dropdownTextStyle, ...dropdownHoverStyle }} className="block rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-[var(--menu-submenu-hover-background)]" />
        ))}
      </div>
    </div>
  );
}

export function MenuLink({
  item,
  siteSlug,
  className,
  onNavigate,
  style,
}: {
  item: PublicMenuItem;
  siteSlug: string;
  className?: string;
  onNavigate?: () => void;
  style?: CSSProperties;
}) {
  const href = buildMenuHref(item, siteSlug);
  if (!href) return <span className={className} style={style}>{item.label}</span>;

  if (isInternalItem(item)) {
    return (
      <Link to={href} className={className} style={style} onClick={onNavigate}>
        {item.label}
      </Link>
    );
  }
  const target = item.target === '_blank' ? '_blank' : undefined;
  return (
    <a href={href} target={target} rel={target ? 'noreferrer' : undefined} className={className} style={style} onClick={onNavigate}>
      {item.label}
    </a>
  );
}
